import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combina clases de Tailwind de forma inteligente, evitando conflictos
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formatea un número como moneda mexicana
 */
export function formatCurrency(
  amount: number,
  options?: { showDecimals?: boolean }
): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: options?.showDecimals ? 2 : 0,
    maximumFractionDigits: options?.showDecimals ? 2 : 0,
  }).format(amount);
}

/**
 * Formatea una fecha en formato legible
 * Maneja correctamente fechas de base de datos (strings como "2025-12-20")
 * evitando problemas de timezone que podrían mostrar el día anterior
 */
export function formatDate(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions
): string {
  let d: Date;
  
  if (typeof date === 'string') {
    // Si es solo fecha (YYYY-MM-DD), agregar T12:00:00 para evitar problemas de timezone
    // Esto asegura que la fecha se muestre correctamente en cualquier timezone
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      d = new Date(date + 'T12:00:00');
    } else {
      d = new Date(date);
    }
  } else {
    d = date;
  }
  
  return d.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'America/Mexico_City', // Forzar timezone de México
    ...options,
  });
}

/**
 * Formatea una hora
 */
export function formatTime(time: string | null | undefined): string {
  if (!time) return '--';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours ?? '0', 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const formattedHour = hour % 12 || 12;
  return `${formattedHour}:${minutes} ${ampm}`;
}

/**
 * Trunca un texto a un número máximo de caracteres
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

/**
 * Capitaliza la primera letra de un string
 */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Genera un ID único
 */
export function generateId(): string {
  return crypto.randomUUID();
}

/**
 * Delay helper para async/await
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Valida si un email es válido
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Formatea un número de teléfono mexicano
 */
export function formatPhoneNumber(phone: string | number): string {
  const phoneStr = String(phone).replace(/\D/g, '');
  if (phoneStr.length === 10) {
    return `(${phoneStr.slice(0, 3)}) ${phoneStr.slice(3, 6)}-${phoneStr.slice(6)}`;
  }
  return phoneStr;
}

/**
 * Formatea RFC mexicano mientras el usuario escribe
 * - Convierte a mayúsculas
 * - Elimina caracteres no alfanuméricos
 * - Limita a 13 caracteres (RFC físico: 13, RFC moral: 12)
 * 
 * @example
 * formatRFC('xaxx-010101-000') // => 'XAXX010101000'
 * formatRFC('abc123456xyz1') // => 'ABC123456XYZ1'
 */
export function formatRFC(value: string): string {
  if (!value) return '';
  
  // Eliminar caracteres no alfanuméricos y convertir a mayúsculas
  let cleaned = value.replace(/[^A-Z0-9Ñ&]/gi, '').toUpperCase();
  
  // Limitar a 13 caracteres
  return cleaned.slice(0, 13);
}

/**
 * Valida formato de RFC mexicano
 * - Persona Física: 13 caracteres (4 letras + 6 dígitos + 3 alfanuméricos)
 * - Persona Moral: 12 caracteres (3 letras + 6 dígitos + 3 alfanuméricos)
 * 
 * @example
 * isValidRFC('XAXX010101000') // => true (Persona Física)
 * isValidRFC('ABC010101000') // => true (Persona Moral)
 * isValidRFC('INVALID') // => false
 */
export function isValidRFC(rfc: string): boolean {
  if (!rfc) return false;
  
  // RFC Persona Física: 4 letras + 6 dígitos + 3 alfanuméricos (13 chars)
  const rfcFisicaRegex = /^[A-ZÑ&]{4}[0-9]{6}[A-Z0-9]{3}$/i;
  
  // RFC Persona Moral: 3 letras + 6 dígitos + 3 alfanuméricos (12 chars)
  const rfcMoralRegex = /^[A-ZÑ&]{3}[0-9]{6}[A-Z0-9]{3}$/i;
  
  return rfcFisicaRegex.test(rfc) || rfcMoralRegex.test(rfc);
}

/**
 * Formatea RFC para visualización (con espacios para legibilidad)
 * 
 * @example
 * formatRFCDisplay('XAXX010101000') // => 'XAXX 010101 000'
 * formatRFCDisplay('ABC010101000') // => 'ABC 010101 000'
 */
export function formatRFCDisplay(rfc: string): string {
  if (!rfc) return '';
  
  const cleaned = formatRFC(rfc);
  
  if (cleaned.length === 13) {
    // Persona Física: XXXX YYMMDD HCA
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 10)} ${cleaned.slice(10, 13)}`;
  } else if (cleaned.length === 12) {
    // Persona Moral: XXX YYMMDD HCA
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 9)} ${cleaned.slice(9, 12)}`;
  }
  
  return cleaned;
}

